import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from './Button.js';
import GlobleRefreshButton from './globleRefreshButton.js';
import useAppStore from '../../store/useAppStore.js';
import { authApi } from '../../services/authApi.js';

const SidebarLayout = ({
  title,
  subtitle,
  menuItems,
  activeItem,
  onMenuClick,
  userRole = 'User',
  children,
  collapsed: initialCollapsed = false,
  hideHeader = false
}) => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [showConnectionDialog, setShowConnectionDialog] = useState(true);

  useEffect(() => {
    const updateOnlineStatus = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    const intervalId = window.setInterval(updateOnlineStatus, 5000);

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (isOnline) {
      setShowConnectionDialog(true);
    }
  }, [isOnline]);

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout failed:', error);
      // Proceed with local cleanup even if server logout fails
    }
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    useAppStore.getState().logout();
    navigate('/');
  };

  const handleRetryConnection = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  return (
    <div className="relative flex h-screen bg-gray-50">
      {!isOnline && showConnectionDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl ring-1 ring-black/10">
            <div className="flex items-start gap-4">
              <div className="mt-1 flex h-12 w-12 items-center justify-center rounded-2xl bg-red-100 text-red-700">
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Connection lost</h2>
                <p className="mt-2 text-sm text-slate-600">
                  Your internet connection appears to be offline. Some app features may not work until the connection is restored.
                </p>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <button
                    type="button"
                    onClick={handleRetryConnection}
                    className="inline-flex items-center justify-center rounded-3xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Retry connection
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsOnline(navigator.onLine)}
                    className="inline-flex items-center justify-center rounded-3xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Check again
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowConnectionDialog(false)}
                    className="inline-flex items-center justify-center rounded-3xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <aside className={`flex flex-col bg-white border-r border-gray-200 shadow-sm transition-all duration-300 ${collapsed ? 'w-16' : 'w-64'}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {!collapsed && (
              <div>
                <div className="text-lg font-bold text-gray-900">{title}</div>
                {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
              </div>
            )}
          </div>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-2">
          <ul className="space-y-1">
            {menuItems.map((item) => {
              const isActive = activeItem === item.label;
              return (
                <li key={item.label}>
                  <button
                    onClick={() => onMenuClick(item.label)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-xl transition-all duration-200 ${
                      isActive
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <span className="text-lg flex-shrink-0">{item.icon}</span>
                    {!collapsed && (
                      <span className="font-medium truncate">{item.label}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Connection status */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <span
              className={`inline-flex h-2.5 w-2.5 rounded-full ${isOnline ? 'bg-emerald-500' : 'bg-red-500'}`}
              aria-hidden="true"
            />
            {!collapsed ? (
              <span>{isOnline ? 'Online' : 'Offline'}</span>
            ) : (
              <span className="sr-only">{isOnline ? 'Online' : 'Offline'}</span>
            )}
          </div>
          {!collapsed && (
            <div className="mt-1 text-xs text-gray-500">Status of the app connection</div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        {!hideHeader && (
          <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 shadow-sm">
            <div className="font-semibold text-gray-900">
              Welcome, {userRole}
            </div>
            <div className="flex items-center gap-3">
              <GlobleRefreshButton />
              <Button variant="danger" size="small" onClick={handleLogout}>
                Logout
              </Button>
            </div>
          </header>
        )}

        {/* Content */}
        <main className={`flex-1 overflow-y-auto p-6 ${hideHeader ? 'pt-0' : ''}`}>
          {children}
        </main>
      </div>
    </div>
  );
};

export default SidebarLayout;